import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import api, { apiBaseURL } from '../../services/api';
import { SMART_COLORS as COLORS, smartCardSx as cardSx } from '../../theme/smartStableTheme';
import { formatDateDisplay } from '../../utils/dateUtils';

const moneyFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});

function formatMoney(value) {
  return `${moneyFormatter.format(Number(value || 0))} SAR`;
}

function formatDate(value) {
  return formatDateDisplay(value);
}

function getAge(value) {
  if (!value) return null;
  const birth = new Date(value);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  if (
    today.getMonth() < birth.getMonth() ||
    (today.getMonth() === birth.getMonth() && today.getDate() < birth.getDate())
  ) {
    age -= 1;
  }
  return Math.max(age, 0);
}

function ageLabel(age) {
  if (age === null) return '-';
  return `${age} years`;
}

function genderLabel(gender, age) {
  if (gender === 'M') return age !== null && age < 3 ? 'Filly' : 'Mare';
  if (gender === 'S') return age !== null && age < 3 ? 'Colt' : 'Stallion';
  return gender || '-';
}

function getImageSrc(image) {
  if (!image) return undefined;
  if (image.startsWith('data') || image.startsWith('http')) return image;
  return `${apiBaseURL}/uploads/${image}`;
}

function normalizeRecord(record, service) {
  return {
    ...record,
    service,
    serviceLabel: service === 'farrier' ? 'Farrier' : 'Vet clinic',
    serviceLabelAr: service === 'farrier' ? 'البيطار' : 'العيادة',
    amount: Number(record.total_amount || 0),
    date: record.record_date,
    detailsPath:
      service === 'farrier'
        ? `/FarrierDetails/${record.record_id}`
        : `/record-details/${record.record_id}`,
  };
}

function ResponsiveGrid({ children, min = 220, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(auto-fit, minmax(${min}px, 1fr))`,
        gap: 2,
        alignItems: 'stretch',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function MetricCard({ icon, title, value, helper, color = COLORS.gold, soft = COLORS.goldSoft }) {
  return (
    <Paper elevation={0} sx={{ ...cardSx, p: 2, minHeight: 104 }}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Avatar sx={{ bgcolor: soft, color, width: 44, height: 44 }}>{icon}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ color, fontWeight: 900 }}>{title}</Typography>
          <Typography variant="h5" sx={{ color: COLORS.text, fontWeight: 900 }}>
            {value}
          </Typography>
          {helper ? (
            <Typography variant="caption" sx={{ color: COLORS.muted }}>
              {helper}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Paper>
  );
}

function TimelineCard({ record }) {
  const isVet = record.service === 'vetclinic';
  const color = isVet ? COLORS.green : COLORS.blue;
  const soft = isVet ? COLORS.greenSoft : COLORS.blueSoft;

  return (
    <Paper
      elevation={0}
      sx={{
        border: `1px solid ${COLORS.line}`,
        borderRadius: 2,
        p: 1.6,
        bgcolor: '#fff',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Avatar sx={{ bgcolor: soft, color, width: 38, height: 38 }}>
          {isVet ? <HealthAndSafetyIcon /> : <StarIcon />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" justifyContent="space-between" gap={1} alignItems="center">
            <Box>
              <Typography sx={{ color: COLORS.text, fontWeight: 900 }}>
                {record.serviceLabel}
              </Typography>
              <Typography variant="caption" className="date-ltr" sx={{ color: COLORS.muted }}>
                {formatDate(record.date)}
              </Typography>
            </Box>
            <Chip
              size="small"
              label={formatMoney(record.amount)}
              sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }}
            />
          </Stack>
          {record.notes ? (
            <Typography sx={{ color: COLORS.text, mt: 1 }}>
              {record.notes}
            </Typography>
          ) : (
            <Typography sx={{ color: COLORS.muted, mt: 1 }}>
              No notes
            </Typography>
          )}
          <Button
            component={Link}
            to={record.detailsPath}
            size="small"
            endIcon={<OpenInNewIcon />}
            sx={{ mt: 0.8, color, fontWeight: 800 }}
          >
            Open details
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function HorseHistory() {
  const { horseId } = useParams();
  const navigate = useNavigate();
  const [horse, setHorse] = useState(null);
  const [ownerName, setOwnerName] = useState('');
  const [farrierRecords, setFarrierRecords] = useState([]);
  const [vetClinicRecords, setVetClinicRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadHistory = async () => {
      setLoading(true);
      setError('');

      try {
        const [horseResponse, farrierResponse, vetClinicResponse] =
          await Promise.all([
            api.get(`/horses/${horseId}`),
            api.get(`/farrier-records-history/${horseId}`),
            api.get(`/vetclinic-records-history/${horseId}`),
          ]);

        if (cancelled) return;
        const horseData = horseResponse.data;
        setHorse(horseData);
        setFarrierRecords(Array.isArray(farrierResponse.data) ? farrierResponse.data : []);
        setVetClinicRecords(Array.isArray(vetClinicResponse.data) ? vetClinicResponse.data : []);

        if (horseData.owner_id) {
          const ownerResponse = await api.get(`/owners/${horseData.owner_id}`);
          if (!cancelled) {
            setOwnerName(
              [ownerResponse.data?.first_name, ownerResponse.data?.last_name]
                .filter(Boolean)
                .join(' ')
            );
          }
        }
      } catch (err) {
        if (!cancelled) setError('Failed to fetch horse history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [horseId]);

  const age = useMemo(() => getAge(horse?.date_of_birth), [horse]);

  const timeline = useMemo(() => {
    const rows = [
      ...farrierRecords.map((record) => normalizeRecord(record, 'farrier')),
      ...vetClinicRecords.map((record) => normalizeRecord(record, 'vetclinic')),
    ];
    return rows.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [farrierRecords, vetClinicRecords]);

  const filteredTimeline = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return timeline
      .filter((record) => (serviceFilter === 'all' ? true : record.service === serviceFilter))
      .filter((record) => {
        if (!needle) return true;
        return [record.serviceLabel, record.serviceLabelAr, record.notes, record.record_id]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(needle);
      });
  }, [search, serviceFilter, timeline]);

  const summary = useMemo(() => {
    const totalAmount = timeline.reduce((sum, record) => sum + record.amount, 0);
    const latest = timeline[0];
    return {
      totalRecords: timeline.length,
      totalAmount,
      farrierCount: farrierRecords.length,
      vetCount: vetClinicRecords.length,
      latest,
    };
  }, [farrierRecords.length, timeline, vetClinicRecords.length]);

  if (loading) {
    return (
      <Box sx={{ minHeight: '60vh', display: 'grid', placeItems: 'center' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress size={26} />
          <Typography>Loading horse history...</Typography>
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: COLORS.page, minHeight: '100%', px: { xs: 2, md: 3 }, py: 3 }}>
      <Box sx={{ maxWidth: 1480, mx: 'auto' }}>
        <Paper
          elevation={0}
          sx={{
            ...cardSx,
            p: { xs: 2, md: 2.5 },
            mb: 2.5,
            background: 'linear-gradient(135deg, #fffdf8 0%, #f4efe5 100%)',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Avatar
                src={getImageSrc(horse?.image)}
                sx={{
                  width: 112,
                  height: 112,
                  bgcolor: COLORS.goldSoft,
                  color: COLORS.gold,
                  fontSize: 36,
                  fontWeight: 900,
                  border: `4px solid ${COLORS.card}`,
                }}
              >
                {(horse?.name_arabic || horse?.name || '-').charAt(0)}
              </Avatar>
              <Box sx={{ textAlign: { xs: 'center', sm: 'start' } }}>
                <Typography variant="h4" sx={{ color: COLORS.text, fontWeight: 900 }}>
                  Horse History
                </Typography>
                <Typography variant="h5" sx={{ color: COLORS.gold, fontWeight: 900, mt: 0.4 }}>
                  {horse?.name || '-'} {horse?.name_arabic ? `| ${horse.name_arabic}` : ''}
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap" justifyContent={{ xs: 'center', sm: 'flex-start' }} sx={{ mt: 1 }}>
                  <Chip label={genderLabel(horse?.gender, age)} sx={{ bgcolor: COLORS.blueSoft, color: COLORS.text, fontWeight: 800 }} />
                  <Chip label={ageLabel(age)} sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800 }} />
                  <Chip label={ownerName || 'Owner -'} sx={{ bgcolor: COLORS.card, color: COLORS.text, fontWeight: 800 }} />
                </Stack>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(`/horse-details/${horseId}`)}
                sx={{
                  borderColor: COLORS.line,
                  color: COLORS.text,
                  fontWeight: 800,
                  '&:hover': { borderColor: COLORS.gold, bgcolor: COLORS.cardSoft },
                }}
              >
                Back to horse
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <ResponsiveGrid min={220} sx={{ mb: 2.5 }}>
          <MetricCard icon={<ReceiptLongIcon />} title="Total records" value={summary.totalRecords} helper="Vet clinic + farrier" />
          <MetricCard icon={<HealthAndSafetyIcon />} title="Vet clinic" value={summary.vetCount} helper="Approved records" color={COLORS.green} soft={COLORS.greenSoft} />
          <MetricCard icon={<StarIcon />} title="Farrier" value={summary.farrierCount} helper="Service records" color={COLORS.blue} soft={COLORS.blueSoft} />
          <MetricCard icon={<ReceiptLongIcon />} title="Total amount" value={formatMoney(summary.totalAmount)} helper={summary.latest ? <>Latest: <span className="date-ltr">{formatDate(summary.latest.date)}</span></> : 'No records'} color={COLORS.rose} soft={COLORS.roseSoft} />
        </ResponsiveGrid>

        <Paper elevation={0} sx={{ ...cardSx, mb: 2.5 }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${COLORS.line}` }}>
            <Stack direction={{ xs: 'column', md: 'row' }} gap={1.2} justifyContent="space-between">
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.2}>
                <TextField
                  select
                  size="small"
                  label="Service"
                  value={serviceFilter}
                  onChange={(event) => setServiceFilter(event.target.value)}
                  sx={{ minWidth: 170 }}
                >
                  <MenuItem value="all">All services</MenuItem>
                  <MenuItem value="vetclinic">Vet clinic</MenuItem>
                  <MenuItem value="farrier">Farrier</MenuItem>
                </TextField>
                <TextField
                  size="small"
                  placeholder="Search notes or record number"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  sx={{ minWidth: { xs: '100%', sm: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Stack>
              <Chip
                label={`${filteredTimeline.length} records`}
                sx={{ bgcolor: COLORS.goldSoft, color: COLORS.text, fontWeight: 800, alignSelf: { xs: 'flex-start', md: 'center' } }}
              />
            </Stack>
          </Box>

          <ResponsiveGrid min={360} sx={{ p: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>
                Timeline
              </Typography>
              <Stack spacing={1.2}>
                {filteredTimeline.slice(0, 10).map((record, index) => (
                  <TimelineCard key={`${record.service}-${record.record_id}-${index}`} record={record} />
                ))}
                {filteredTimeline.length === 0 ? (
                  <Typography sx={{ color: COLORS.muted, py: 4, textAlign: 'center' }}>
                    No records available.
                  </Typography>
                ) : null}
              </Stack>
            </Box>

            <Box>
              <Typography variant="h6" sx={{ color: COLORS.text, fontWeight: 900, mb: 1.5 }}>
                Detailed table
              </Typography>
              <TableContainer sx={{ border: `1px solid ${COLORS.line}`, borderRadius: 2 }}>
                <Table size="small">
                  <TableHead
                    sx={{
                      '& .MuiTableCell-root': {
                        bgcolor: COLORS.gold,
                        color: '#fff',
                        fontWeight: 900,
                        borderBottom: 0,
                      },
                    }}
                  >
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Service</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Notes</TableCell>
                      <TableCell align="center">Open</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredTimeline.map((record, index) => (
                      <TableRow key={`${record.service}-table-${record.record_id}-${index}`} hover>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={record.serviceLabel}
                            sx={{
                              bgcolor: record.service === 'vetclinic' ? COLORS.greenSoft : COLORS.blueSoft,
                              color: COLORS.text,
                              fontWeight: 800,
                            }}
                          />
                        </TableCell>
                        <TableCell><span className="date-ltr">{formatDate(record.date)}</span></TableCell>
                        <TableCell align="right">{formatMoney(record.amount)}</TableCell>
                        <TableCell sx={{ maxWidth: 260 }}>
                          <Typography noWrap>{record.notes || '-'}</Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Open details">
                            <IconButton component={Link} to={record.detailsPath} sx={{ color: COLORS.gold }}>
                              <OpenInNewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredTimeline.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4, color: COLORS.muted }}>
                          No records available.
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </ResponsiveGrid>
        </Paper>
      </Box>
    </Box>
  );
}
